import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../Components/Header';

const OTPContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
  }
`;

const OTPContent = styled.div`
  padding: 20px 16px 24px 16px;
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 20px 16px 24px 16px;
    min-height: calc(100vh - 60px);
  }
`;

const OTPImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const OTPImage = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
  
  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
  }
`;

const OTPTitle = styled.h1`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

const OTPSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
  text-align: center;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ResendLinkContainer = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const ResendLink = styled.span`
  color: #00BBD3;
  cursor: pointer;
  text-decoration: underline;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  
  &:hover {
    color: #009bb3;
  }
`;

const OTPForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const OTPInputGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const OTPInput = styled.input`
  width: 56px;
  height: 56px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  background-color: white;
  text-align: center;
  transition: border-color 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 18px;
    border-radius: 8px;
  }

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &.filled {
    border-color: #00BBD3;
    background-color: #f0f9ff;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const ConfirmButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

  &:hover {
    background-color: #009bb3;
  }

  &:active {
    background-color: #008aa0;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4757;
  font-size: 14px;
  margin-top: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  display: ${props => props.show ? 'block' : 'none'};
`;

const OTPPage = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState(false);

  // Mock OTP
  const MOCK_OTP = '0000';

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError(false); // Clear error when user types

      // Auto focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 4) {
      setError(true);
      return;
    }

    if (otpCode === MOCK_OTP) {
      console.log('OTP ถูกต้อง!');
      // ไปหน้า Success
      navigate('/success');
    } else {
      setError(true);
    }
  };

  const handleResend = () => {
    console.log('ส่ง OTP ใหม่');
    setOtp(['', '', '', '']);
    setError(false);
    // Focus first input
    const firstInput = document.getElementById('otp-0');
    if (firstInput) firstInput.focus();
  };

  return (
    <OTPContainer>
      <Header />
      
      <OTPContent>
        <OTPImageContainer>
          <OTPImage src="./OTP.png" alt="OTP Verification" />
        </OTPImageContainer>

        <OTPTitle>กรุณากรอกรหัส OTP ที่ส่งทาง SMS</OTPTitle>
        <OTPSubtitle>หากยังไม่ได้รหัส SMS</OTPSubtitle>
        
        <ResendLinkContainer>
          <ResendLink onClick={handleResend}>กดส่งใหม่</ResendLink>
        </ResendLinkContainer>
        
        <OTPForm onSubmit={handleSubmit}>
          <OTPInputGroup>
            {otp.map((digit, index) => (
              <OTPInput
                key={index}
                id={`otp-${index}`}
                type="number"
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                maxLength="1"
                className={digit ? 'filled' : ''}
              />
            ))}
          </OTPInputGroup>
          
          <ErrorMessage show={error}>
            รหัส OTP ไม่ถูกต้อง กรุณาลองอีกครั้ง
          </ErrorMessage>
          
          <ConfirmButton type="submit">
            ยืนยัน
          </ConfirmButton>
        </OTPForm>
      </OTPContent>
    </OTPContainer>
  );
};

export default OTPPage;